import React, { useEffect, useState } from "react";
import { Cookie, X, ShieldCheck, BarChart3, Megaphone } from "lucide-react";
import "./cookie.css";

const CONSENT_KEY = "clipsymphony:cookie-consent:v1";

type ConsentRecord = {
  essential: true;
  analytical: boolean;
  marketing: boolean;
  decidedAt: string;
};

type PrefsState = {
  analytical: boolean;
  marketing: boolean;
};

type Category = {
  id: "essential" | "analytical" | "marketing";
  icon: typeof ShieldCheck;
  title: string;
  body: string;
  locked?: boolean;
};

function loadConsent(): ConsentRecord | null {
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.essential !== true || typeof parsed.analytical !== "boolean" || typeof parsed.marketing !== "boolean") {
      return null;
    }
    return {
      essential: true,
      analytical: parsed.analytical,
      marketing: parsed.marketing,
      decidedAt: typeof parsed.decidedAt === "string" ? parsed.decidedAt : new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

const CATEGORIES: Category[] = [
  {
    id: "essential",
    icon: ShieldCheck,
    title: "Essential",
    locked: true,
    body: "Necessary for the website to function and cannot be switched off. They keep track of your login status and remember your language preference.",
  },
  {
    id: "analytical",
    icon: BarChart3,
    title: "Analytical",
    body: "Help us understand how you use the website so we can improve it — counting visitors, which pages you visit, and how long you stay.",
  },
  {
    id: "marketing",
    icon: Megaphone,
    title: "Marketing",
    body: "Used to show you relevant advertising based on the sites you visit and your interests.",
  },
];

export default function CookieConsent() {
  const [consent, setConsent] = useState<ConsentRecord | null>(() => loadConsent());
  const [managing, setManaging] = useState(false);
  const [prefs, setPrefs] = useState<PrefsState>({ analytical: false, marketing: false });

  useEffect(() => {
    if (!managing) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setManaging(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [managing]);

  useEffect(() => {
    const syncConsent = () => setConsent(loadConsent());
    const onStorage = (e: StorageEvent) => {
      if (e.key === CONSENT_KEY) syncConsent();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  if (consent) return null;

  const decide = (choice: PrefsState) => {
    const value: ConsentRecord = { essential: true, ...choice, decidedAt: new Date().toISOString() };
    try {
      window.localStorage.setItem(CONSENT_KEY, JSON.stringify(value));
    } catch {
      // Ignore storage failures; the in-memory decision still dismisses the banner.
    }
    setConsent(value);
    setManaging(false);
  };

  const openManage = () => {
    const existing = loadConsent();
    setPrefs({
      analytical: existing?.analytical ?? false,
      marketing: existing?.marketing ?? false,
    });
    setManaging(true);
  };

  const acceptAll = () => decide({ analytical: true, marketing: true });
  const rejectNonEssential = () => decide({ analytical: false, marketing: false });
  const savePreferences = () => decide({ analytical: prefs.analytical, marketing: prefs.marketing });

  return (
    <div className="cc-root" role="dialog" aria-modal={managing} aria-label="Cookie consent">
      {managing && <div className="cc-overlay" onClick={() => setManaging(false)} />}

      <div className={`cc-panel ${managing ? "cc-panel-manage" : "cc-panel-banner"}`}>
        {!managing ? (
          <>
            <div className="cc-banner-text">
              <div className="cc-banner-icon" aria-hidden="true"><Cookie size={20} /></div>
              <div className="cc-banner-textcol">
                <h2 className="cc-title">Cookie consent & data processing</h2>
                <p className="cc-body">
                  ClipSymphony uses cookies to improve your experience. Some are essential for the site to work;
                  others help us understand usage and show relevant content. By choosing "Accept all" you agree
                  to processing as described in our <a href="/app/privacy-policy" className="cc-link">privacy policy</a>.
                </p>
              </div>
            </div>
            <div className="cc-banner-actions">
              <button className="cc-btn cc-btn-ghost" onClick={openManage}>Manage preferences</button>
              <button className="cc-btn cc-btn-secondary" onClick={rejectNonEssential}>Reject non-essential</button>
              <button className="cc-btn cc-btn-primary" onClick={acceptAll}>Accept all</button>
            </div>
          </>
        ) : (
          <>
            <div className="cc-manage-head">
              <h2 className="cc-title">Manage your data & privacy settings</h2>
              <button className="cc-close" aria-label="Close" onClick={() => setManaging(false)}><X size={18} /></button>
            </div>
            <p className="cc-body cc-manage-intro">
              Choose which cookies you allow. If you decline some, certain features may not be available.
            </p>

            <div className="cc-categories">
              {CATEGORIES.map((cat) => {
                const Icon = cat.icon;
                const on = cat.locked ? true : prefs[cat.id as keyof PrefsState];
                return (
                  <div key={cat.id} className="cc-category">
                    <div className="cc-category-icon" aria-hidden="true"><Icon size={16} /></div>
                    <div className="cc-category-text">
                      <div className="cc-category-title">
                        {cat.title}
                        {cat.locked && <span className="cc-always-on">Always on</span>}
                      </div>
                      <p className="cc-category-body">{cat.body}</p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={on}
                      aria-label={cat.title}
                      disabled={cat.locked}
                      className={`cc-switch ${on ? "cc-switch-on" : ""} ${cat.locked ? "cc-switch-locked" : ""}`}
                      onClick={() => !cat.locked && setPrefs((prev) => ({ ...prev, [cat.id]: !prev[cat.id as keyof PrefsState] }))}
                    >
                      <span className="cc-switch-knob" />
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="cc-manage-actions">
              <button className="cc-btn cc-btn-secondary" onClick={rejectNonEssential}>Reject non-essential</button>
              <button className="cc-btn cc-btn-ghost" onClick={savePreferences}>Save preferences</button>
              <button className="cc-btn cc-btn-primary" onClick={acceptAll}>Accept all</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
